/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo } from 'react';
import { Typography, Stack, Grid, IconButton, Skeleton, FormLabel, useTheme } from '@mui/material';
import { ComplianceTaskCheckbox } from '@/components/cards/DealCardValues/ComplianceTaskCheckbox';
import { useGetDealComplianceTasksQuery } from '@/features/engagements/engagementsApi';
import ComplianceAccordion from '@/components/cards/ComplianceAccordion';
import { useObjectContext } from '@/hooks/useObjectContext';
import { usePipelineContext } from '@/context/pipelineContext';
import { getStepStageIds } from '@/utils/constants';
import { CompleteDealTasks } from '@/components/dialogs/CompleteDealDialog/CompleteDealTasks';
import { backgroundColour } from '@/app/theme';

const CompleteDealStepsToComplete = ({ setOpen }) => {
	const theme = useTheme();
	const isDarkMode = theme.palette.mode === 'dark';

	const { dealId, dealRow } = useObjectContext();

	const { data, ...tasksQuery } = useGetDealComplianceTasksQuery(
		{ dealId, includeInactive: false },
		{ skip: !dealId },
	);

	const pipelineId = dealRow?.pipeline;
	const { stages, checkEndorsementStage } = usePipelineContext(pipelineId);

	const isNewBusiness = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isRenewal = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_RENEWALS;

	const isEndorsementStage = checkEndorsementStage(pipelineId);

	const complianceTasks = useMemo(() => {
		const tasks = data ?? [];

		// First filter for active tasks
		const activeTasks = tasks.filter((task) => {
			// Check if task is active (isActive === true)
			return task.questionData?.isActive === true;
		});

		const endorsementStageIds =
			stages
				?.filter((stage) => stage.label?.toLowerCase()?.includes('endorsement'))
				?.map((stage) => stage.id) ?? [];

		if (isEndorsementStage) {
			return activeTasks.filter((task) => task.stageId === dealRow?.dealStage);
		} else if (isNewBusiness || isRenewal) {
			return activeTasks.filter((task) => !endorsementStageIds.includes(task.stageId));
		}

		return activeTasks;
	}, [data, isEndorsementStage, isNewBusiness, isRenewal, dealRow?.dealStage, stages]);

	const taskList = useMemo(() => {
		if (!stages || !complianceTasks || complianceTasks.length === 0) return [];

		const stageList = stages.reduce((acc, stage) => {
			const tasks = complianceTasks.filter((t) => getStepStageIds(t).includes(stage.id));

			if (tasks.length > 0) {
				acc.push({
					stageId: stage.id,
					order: stage.order,
					label: stage.label,
					tasks: tasks.sort((a, b) => {
						const aOrder = a.questionData?.order ?? 0;
						const bOrder = b.questionData?.order ?? 0;
						return aOrder - bOrder;
					}),
				});
			}
			return acc;
		}, []);

		return stageList.sort((a, b) => a.order - b.order);
	}, [stages, complianceTasks]);

	useEffect(() => {
		if (tasksQuery.isSuccess) {
			setOpen(taskList.length > 0);
		}
	}, [tasksQuery.isSuccess, taskList.length, setOpen]);

	// Show nothing if no tasks or still loading
	if (tasksQuery.isSuccess && taskList.length === 0) {
		return <></>;
	}

	return (
		<>
			<Stack pb={1}>
				<FormLabel>Steps to complete</FormLabel>
				<CompleteDealTasks />
			</Stack>

			{tasksQuery.isUninitialized || tasksQuery.isLoading ? (
				<Skeleton width='100%' />
			) : (
				<Stack
					spacing={1}
					sx={{
						'& .compliance-accordion-title': {
							fontWeight: 500,
							color: isDarkMode
								? theme.palette.folioWhite
								: theme.palette.folioBlack100,
							fontSize: '13px',
						},
						...(isDarkMode && {
							'& .MuiFormControlLabel-label': {
								color: theme.palette.text.secondary,
							},
						}),
					}}
				>
					{taskList.map(({ stageId, label, tasks }) => (
						<ComplianceAccordion
							title={label}
							tasks={tasks ?? []}
							key={`compliance-accordion-${stageId}`}
							accordionMode={false}
							truncateTotal
						>
							<Stack
								sx={{ fontSize: '12px', '& .MuiFormControlLabel-root': { mx: 0 } }}
							>
								{tasks.map((task) => (
									<ComplianceTaskCheckbox key={task.documentId} task={task} />
								))}
							</Stack>
						</ComplianceAccordion>
					))}
				</Stack>
			)}
		</>
	);
};
CompleteDealStepsToComplete.displayName = 'CompleteDealStepsToComplete';
export default CompleteDealStepsToComplete;
